/**
 * ResolveTicketModal - Fluxo do botão "Resolver" reformado (Plano A — Task 9).
 *
 * Chama `useResolveTicket` (`POST /tickets/:id/resolve`, Task 5 do backend)
 * com um resumo de resolução OPCIONAL. Quando o pipeline do ticket não tem
 * nenhuma etapa `category: 'resolved'`, o backend responde 422 com
 * `code: 'NO_RESOLVED_STAGE'` — este modal troca de fase e oferece o CTA
 * "Criar etapa de resolução" (gated por `ticket-pipelines.update`), que cria
 * a stage `{ name: 'Resolvido', category: 'resolved', color: '#10B981' }` via
 * `useCreateStage` e repete o resolve automaticamente.
 *
 * Modal custom (não o `Modal` shared) — segue o MESMO padrão hand-rolled já
 * usado neste módulo em `StageChangeConfirmation.tsx` (tema claro fixo via
 * hex hardcoded). Como este modal tem um `Textarea` shared (que lê CSS
 * variables `--theme-input-*`), o container recebe o MESMO override de tema
 * claro fixo usado em `TicketFieldPopover.tsx` — sem isso o textarea herdaria
 * o tema dark da página por trás do overlay.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Textarea } from '@/shared/components/ui/Textarea';
import { usePermissions } from '@/hooks';
import { useCreateStage } from '@/modules/helpdesk/pipelines/hooks/useStages';
import { useResolveTicket } from '../hooks/useTickets';
import type { PopulatedTicket } from '../types';
import { Icon } from '@/shared/icons';

const NO_RESOLVED_STAGE_CODE = 'NO_RESOLVED_STAGE';

const LIGHT_THEME_OVERRIDE: React.CSSProperties = {
  '--theme-text': '#1e293b',
  '--theme-muted-text': '#6b7280',
  '--theme-input-background': '#ffffff',
  '--theme-input-text': '#1e293b',
  '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
  '--theme-input-focus': '#8b5cf6',
  '--theme-error-color': '#ef4444',
  '--theme-card-background': '#ffffff',
  '--theme-card-text': '#334155',
  '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
  '--theme-primary': '#8b5cf6',
} as React.CSSProperties;

export interface ResolveTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: PopulatedTicket;
}

export const ResolveTicketModal: React.FC<ResolveTicketModalProps> = ({ isOpen, onClose, ticket }) => {
  const [resolutionSummary, setResolutionSummary] = useState('');
  const [noStageError, setNoStageError] = useState(false);

  const resolveMutation = useResolveTicket();
  const createStageMutation = useCreateStage();
  const { hasModulePermission } = usePermissions();
  const canCreateStage = hasModulePermission('ticket-pipelines', 'update');

  useEffect(() => {
    if (isOpen) {
      setResolutionSummary('');
      setNoStageError(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isLoading = resolveMutation.isPending || createStageMutation.isPending;

  const handleResolve = async () => {
    try {
      await resolveMutation.mutateAsync({
        id: ticket.id,
        data: { resolutionSummary: resolutionSummary.trim() || undefined },
      });
      toast.success('Ticket resolvido!');
      onClose();
    } catch (error) {
      if (axios.isAxiosError(error) && (error.response?.data as { code?: string } | undefined)?.code === NO_RESOLVED_STAGE_CODE) {
        setNoStageError(true);
      } else {
        toast.error('Erro ao resolver ticket');
      }
    }
  };

  const handleCreateStageAndResolve = async () => {
    if (!ticket.pipelineId) {
      toast.error('Ticket sem pipeline associado — não é possível criar a etapa');
      return;
    }

    try {
      await createStageMutation.mutateAsync({
        pipelineId: ticket.pipelineId,
        name: 'Resolvido',
        category: 'resolved',
        color: '#10B981',
      });
    } catch {
      // useCreateStage já mostra o toast de erro
      return;
    }

    try {
      await resolveMutation.mutateAsync({
        id: ticket.id,
        data: { resolutionSummary: resolutionSummary.trim() || undefined },
      });
      toast.success('Ticket resolvido!');
      onClose();
    } catch {
      toast.error('Etapa criada, mas houve um erro ao resolver o ticket. Tente novamente.');
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center z-[60] p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      data-testid="ticket-detail-resolve-modal"
    >
      <div
        className="rounded-lg shadow-xl max-w-md w-full"
        style={{ backgroundColor: '#ffffff', ...LIGHT_THEME_OVERRIDE }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: '#e5e7eb' }}>
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundColor: noStageError ? '#fef3c7' : '#dcfce7' }}
            >
              <Icon
                name={noStageError ? 'alert-triangle' : 'check'}
                className="w-5 h-5"
                style={{ color: noStageError ? '#d97706' : '#059669' }}
              />
            </div>
            <h2 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
              {noStageError ? 'Nenhuma etapa de resolução' : 'Resolver ticket'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded transition-colors"
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            disabled={isLoading}
            data-testid="ticket-detail-resolve-modal-close"
          >
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {noStageError ? (
            <>
              <p className="mb-3 text-sm" style={{ color: '#1e293b' }}>
                O pipeline deste ticket não possui uma etapa com categoria <strong>&quot;Resolvido&quot;</strong>.
                {canCreateStage
                  ? ' Crie a etapa agora para poder resolver este e os próximos tickets deste pipeline.'
                  : ' Peça a um administrador para criar essa etapa no pipeline.'}
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  Etapa a ser criada: <strong>Resolvido</strong> (categoria "resolved").
                </p>
              </div>
            </>
          ) : (
            <>
              <p className="mb-3 text-sm" style={{ color: '#1e293b' }}>
                O ticket <strong>#{ticket.ticketNumber}</strong> será movido para a etapa de resolução do pipeline.
              </p>
              <Textarea
                label="Resumo da resolução (opcional)"
                value={resolutionSummary}
                onChange={(e) => setResolutionSummary(e.target.value)}
                placeholder="Descreva como o problema foi resolvido..."
                rows={4}
                maxLength={2000}
                disabled={isLoading}
                data-testid="ticket-detail-resolve-modal-summary"
              />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t" style={{ borderColor: '#e5e7eb' }}>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border disabled:opacity-50 transition-colors"
            style={{ color: '#1e293b', backgroundColor: '#ffffff', borderColor: '#e5e7eb' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
            disabled={isLoading}
            data-testid="ticket-detail-resolve-modal-cancel"
          >
            Cancelar
          </button>
          {noStageError ? (
            canCreateStage && (
              <button
                type="button"
                onClick={() => { void handleCreateStageAndResolve(); }}
                className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 transition-colors"
                style={{ backgroundColor: '#8b5cf6' }}
                onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = '#7c3aed'; }}
                onMouseLeave={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = '#8b5cf6'; }}
                disabled={isLoading}
                data-testid="ticket-detail-resolve-modal-create-stage"
              >
                {isLoading ? 'Criando...' : 'Criar etapa de resolução'}
              </button>
            )
          ) : (
            <button
              type="button"
              onClick={() => { void handleResolve(); }}
              className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 transition-colors"
              style={{ backgroundColor: '#8b5cf6' }}
              onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = '#7c3aed'; }}
              onMouseLeave={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = '#8b5cf6'; }}
              disabled={isLoading}
              data-testid="ticket-detail-resolve-modal-confirm"
            >
              {isLoading ? 'Resolvendo...' : 'Resolver'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

ResolveTicketModal.displayName = 'ResolveTicketModal';
